import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { LeadDedupeSettings, LeadStatusCategory, LeadStatusResponse } from '@orion/contracts';
import { Button, Dialog, Input, Select, Switch } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { useSettingsQuery, useUpdateSettings } from '../settings/api.js';
import {
  useCreateLeadSource,
  useCreateLeadStatus,
  useDeleteLeadStatus,
  useLeadSourcesQuery,
  useLeadStatusesQuery,
  useReorderLeadStatuses,
  useUpdateLeadSource,
  useUpdateLeadStatus,
} from './api.js';

const isDesignLeadConfigPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminLeadConfig';

const CATEGORY_LABEL: Record<LeadStatusCategory, string> = {
  open: 'Open',
  qualified: 'Qualified',
  converted: 'Closed-won',
  lost: 'Closed-lost',
  disqualified: 'Closed-lost',
};
const CATEGORY_STYLE: Record<LeadStatusCategory, string> = {
  open: 'border border-border text-text-secondary',
  qualified: 'bg-violet-soft text-violet',
  converted: 'bg-success-soft text-success',
  lost: 'bg-danger-soft text-danger',
  disqualified: 'bg-danger-soft text-danger',
};
const CATEGORY_OPTIONS: Array<{ value: LeadStatusCategory; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'lost', label: 'Lost' },
  { value: 'disqualified', label: 'Disqualified' },
];

const TABS = [
  { key: 'statuses', label: 'Statuses' },
  { key: 'sources', label: 'Sources' },
  { key: 'fields', label: 'Fields' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'assignment', label: 'Assignment rules' },
  { key: 'duplicates', label: 'Duplicates' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

function DragHandle() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true" className="text-text-faint">
      <circle cx="9" cy="6" r="0.8" fill="currentColor" />
      <circle cx="15" cy="6" r="0.8" fill="currentColor" />
      <circle cx="9" cy="12" r="0.8" fill="currentColor" />
      <circle cx="15" cy="12" r="0.8" fill="currentColor" />
      <circle cx="9" cy="18" r="0.8" fill="currentColor" />
      <circle cx="15" cy="18" r="0.8" fill="currentColor" />
    </svg>
  );
}

function EditStatusDialog({ status, onClose }: { status: LeadStatusResponse; onClose: () => void }) {
  const update = useUpdateLeadStatus();
  const [name, setName] = useState(status.name);
  const [category, setCategory] = useState(status.category);
  const [color, setColor] = useState(status.color);

  return (
    <Dialog
      open
      title={`Edit status: ${status.name}`}
      onClose={onClose}
      actions={
        <Button
          size="sm"
          loading={update.isPending}
          onClick={async () => {
            await update.mutateAsync({ id: status.id, input: { name, category, color } });
            onClose();
          }}
        >
          Save
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} disabled={status.isSystem} />
        <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value as LeadStatusCategory)} options={CATEGORY_OPTIONS} disabled={status.isSystem} />
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
          Colour
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-16 cursor-pointer rounded-control border border-border-strong" />
        </label>
        {status.isSystem && <p className="text-xs text-text-muted">System statuses keep their name and category — only the colour can change.</p>}
      </div>
    </Dialog>
  );
}

function StatusesTab() {
  const { data: statuses } = useLeadStatusesQuery();
  const createStatus = useCreateLeadStatus();
  const updateStatus = useUpdateLeadStatus();
  const deleteStatus = useDeleteLeadStatus();
  const reorder = useReorderLeadStatuses();
  const [editing, setEditing] = useState<LeadStatusResponse | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<LeadStatusCategory>('open');

  const rows = statuses ?? [];
  const totalLeads = rows.reduce((n, s) => n + s.usageCount, 0);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    if (rows[index]!.isSystem || rows[target]!.isSystem) return;
    const orderedIds = rows.map((s) => s.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target]!, orderedIds[index]!];
    reorder.mutate({ orderedIds });
  };

  return (
    <section aria-label="Lead statuses" className="overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
      <div className="flex h-13 items-center justify-between px-5">
        <div className="flex items-baseline gap-2">
          <h2 className="text-[15px] font-semibold">Lead statuses</h2>
          <span className="text-[13px] text-text-muted">Drag to reorder</span>
        </div>
        <span className="text-xs text-text-muted">{totalLeads} leads total</span>
      </div>
      <div className="grid h-10 items-center px-4 text-xs font-semibold leading-3.5 text-text-muted" style={{ gridTemplateColumns: '28px minmax(0,1.3fr) minmax(0,1fr) 60px 76px 72px 60px', background: 'var(--color-surface-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
        <span />
        <span>Status</span>
        <span>Category</span>
        <span className="text-center">Default</span>
        <span className="text-center">Counts as qualified</span>
        <span>Usage</span>
        <span />
      </div>
      {rows.map((s, i) => (
        <div key={s.id} className="grid h-12 items-center border-b border-divider px-4 text-sm last:border-b-0" style={{ gridTemplateColumns: '28px minmax(0,1.3fr) minmax(0,1fr) 60px 76px 72px 60px', background: s.isSystem ? 'var(--color-surface-subtle)' : undefined }}>
          {s.isSystem ? (
            <span role="img" aria-label="Locked position">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-text-faint">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </span>
          ) : (
            <span className="flex flex-col">
              <button type="button" onClick={() => move(i, -1)} aria-label={`Move ${s.name} up`} className="h-3 text-text-faint hover:text-text-secondary">
                <DragHandle />
              </button>
            </span>
          )}
          <span className="flex items-center gap-2 font-semibold">
            <span className="size-2 rounded-full" style={{ background: s.color }} />
            {s.name}
            {s.isSystem && <span className="rounded-xs bg-surface-subtle px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-text-muted">System</span>}
          </span>
          <span>
            <span className={`inline-flex h-5.5 items-center rounded-control px-2 text-xs font-medium ${CATEGORY_STYLE[s.category]}`}>{CATEGORY_LABEL[s.category]}</span>
          </span>
          <span className="flex justify-center">
            <input
              type="radio"
              name="default-status"
              checked={s.isDefault}
              disabled={s.category === 'converted'}
              aria-label={`Default for new leads: ${s.name}`}
              onChange={() => updateStatus.mutate({ id: s.id, input: { isDefault: true } })}
              style={{ accentColor: 'var(--color-primary)' }}
            />
          </span>
          <span className="flex justify-center">
            <Switch
              checked={s.countsAsQualified}
              disabled={s.category === 'converted'}
              onChange={(v) => updateStatus.mutate({ id: s.id, input: { countsAsQualified: v } })}
              label={`${s.name} counts as qualified`}
              hideLabel
            />
          </span>
          <span className="tabular-nums text-[13px] text-text-secondary">{s.usageCount} leads</span>
          <span className="flex justify-end gap-0.5">
            {s.category !== 'converted' && (
              <button type="button" aria-label={`Edit ${s.name}`} onClick={() => setEditing(s)} className="flex size-7 items-center justify-center rounded-chip text-text-secondary hover:bg-surface-subtle">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 20h4L19 9l-4-4L4 16z" />
                  <path d="m13 7 4 4" />
                </svg>
              </button>
            )}
            {!s.isSystem && (
              <span className="group relative flex">
                <button
                  type="button"
                  disabled={s.usageCount > 0}
                  aria-label={`Delete ${s.name}`}
                  aria-describedby={s.usageCount > 0 ? `tip-${s.id}` : undefined}
                  onClick={() => deleteStatus.mutate(s.id)}
                  className="flex size-7 items-center justify-center rounded-chip text-text-secondary hover:bg-surface-subtle disabled:bg-surface-subtle disabled:text-text-faint"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                  </svg>
                </button>
                {s.usageCount > 0 && (
                  <span
                    id={`tip-${s.id}`}
                    role="tooltip"
                    className={`absolute -right-1.5 top-9 z-10 whitespace-nowrap rounded-control bg-night px-2.5 py-1.5 text-xs font-medium text-white shadow-dialog ${isDesignLeadConfigPreview && s.id === 'st-new' ? '' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'}`}
                  >
                    <span className="absolute -top-1 right-4 size-2 rotate-45 bg-night" />
                    Reassign {s.usageCount} leads first
                  </span>
                )}
              </span>
            )}
          </span>
        </div>
      ))}
      <div className="flex h-12 items-center justify-between px-3">
        {showAdd ? (
          <div className="flex flex-1 items-center gap-2">
            <Input label="Status name" hideLabel value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Status name" className="w-40" />
            <Select label="Category" hideLabel value={newCategory} onChange={(e) => setNewCategory(e.target.value as LeadStatusCategory)} options={CATEGORY_OPTIONS} className="w-36" />
            <Button
              size="sm"
              loading={createStatus.isPending}
              onClick={async () => {
                if (!newName.trim()) return;
                await createStatus.mutateAsync({ name: newName.trim(), category: newCategory, color: '#454A54', isDefault: false, countsAsQualified: newCategory === 'qualified' });
                setNewName('');
                setShowAdd(false);
              }}
            >
              Add
            </Button>
          </div>
        ) : (
          <button type="button" onClick={() => setShowAdd(true)} className="flex h-8 items-center gap-1.5 rounded-chip px-2 text-[13px] font-semibold text-primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add status
          </button>
        )}
        <span className="text-xs text-text-muted">Converted is set by the convert flow and can&rsquo;t be moved or removed.</span>
      </div>
      {editing && <EditStatusDialog status={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function SourcesTab() {
  const { data: sources } = useLeadSourcesQuery();
  const createSource = useCreateLeadSource();
  const updateSource = useUpdateLeadSource();
  const [newName, setNewName] = useState('');

  return (
    <section aria-label="Lead sources" className="overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
      <div className="flex h-13 items-center px-5">
        <h2 className="text-[15px] font-semibold">Lead sources</h2>
      </div>
      <div className="grid h-10 items-center px-4 text-xs font-semibold text-text-muted" style={{ gridTemplateColumns: '1.4fr 1fr 90px 60px', background: 'var(--color-surface-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
        <span>Name</span>
        <span>Channel</span>
        <span className="text-center">Active</span>
        <span>Usage</span>
      </div>
      {(sources ?? []).map((s) => (
        <div key={s.id} className="grid h-11 items-center border-b border-divider px-4 text-sm last:border-b-0" style={{ gridTemplateColumns: '1.4fr 1fr 90px 60px' }}>
          <span className="font-semibold">{s.name}</span>
          <span className="text-text-secondary">{s.channel ?? '—'}</span>
          <span className="flex justify-center">
            <Switch checked={s.isActive} onChange={(v) => updateSource.mutate({ id: s.id, input: { isActive: v } })} label={`${s.name} active`} hideLabel />
          </span>
          <span className="tabular-nums text-[13px] text-text-secondary">{s.usageCount}</span>
        </div>
      ))}
      <div className="flex h-12 items-center gap-2 px-3">
        <Input label="New source" hideLabel value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Source name" className="w-48" />
        <Button
          size="sm"
          loading={createSource.isPending}
          onClick={async () => {
            if (!newName.trim()) return;
            await createSource.mutateAsync({ name: newName.trim(), isActive: true });
            setNewName('');
          }}
        >
          Add
        </Button>
      </div>
    </section>
  );
}

function ComingSoon({ title, note }: { title: string; note: string }) {
  return (
    <section className="flex flex-col items-center justify-center gap-2 rounded-dialog border border-dashed border-border-strong bg-surface p-12 text-center shadow-card">
      <h2 className="text-[15px] font-semibold">{title}</h2>
      <p className="max-w-md text-sm text-text-muted">{note}</p>
    </section>
  );
}

function AssignmentRulesCard() {
  return (
    <section aria-labelledby="as-h" className="flex flex-col gap-3 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
      <h2 id="as-h" className="text-[15px] font-semibold">
        Assignment rules
      </h2>
      <div className="flex items-center gap-2.5 rounded-control border border-divider bg-surface-subtle px-3 py-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-chip bg-primary-soft">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-primary">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
          </svg>
        </span>
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">Manual assignment</span>
          <span className="text-xs leading-4 text-text-muted">New leads are assigned by a rep — no automatic routing yet.</span>
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 text-[13px] font-medium">
        <span>Skip users marked out of office</span>
        <span className="text-xs text-text-faint">Not available</span>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Rules · not configured</span>
        <div className="flex h-9 items-center rounded-control border border-dashed border-border-strong px-2.5 text-[13px] text-text-muted">Every lead needs a manual owner today</div>
        <div className="flex h-9 items-center rounded-control border border-dashed border-border-strong px-2.5 text-[13px] text-text-muted">Round-robin/source rules aren&rsquo;t built yet</div>
      </div>
    </section>
  );
}

function DuplicatesCard() {
  const { data, isLoading } = useSettingsQuery<LeadDedupeSettings>('lead_dedupe');
  const update = useUpdateSettings<LeadDedupeSettings>('lead_dedupe');
  const [draft, setDraft] = useState<LeadDedupeSettings | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(data);
  }, [data, draft]);

  if (isLoading || !draft) return null;

  const save = (patch: Partial<LeadDedupeSettings>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    update.mutate(next);
  };

  return (
    <section aria-labelledby="dup-h" className="flex flex-col gap-3 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
      <h2 id="dup-h" className="text-[15px] font-semibold">
        Duplicate detection
      </h2>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Match on</legend>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={draft.matchOnEmail} onChange={(e) => save({ matchOnEmail: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
          <span className="font-semibold">Email</span>
          <span className="text-text-muted">exact</span>
        </label>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={draft.matchOnPhone} onChange={(e) => save({ matchOnPhone: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
          <span className="font-semibold">Phone</span>
          <span className="text-text-muted">normalised E.164</span>
        </label>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={draft.matchOnNameCompany} onChange={(e) => save({ matchOnNameCompany: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
          <span className="font-semibold">Name + Company</span>
          <span className="text-text-muted">
            fuzzy ≥{' '}
            <input
              type="number"
              min={0.5}
              max={1}
              step={0.05}
              value={draft.nameCompanyThreshold}
              onChange={(e) => save({ nameCompanyThreshold: Number(e.target.value) })}
              className="w-14 rounded-xs border border-border-strong px-1 font-mono text-xs"
            />
          </span>
        </label>
      </fieldset>
      <div className="border-t border-divider" />
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">On match</legend>
        {(
          [
            { value: 'warn', label: 'Warn and let user choose' },
            { value: 'block', label: 'Block the new record' },
            { value: 'merge', label: 'Merge automatically*' },
          ] as const
        ).map((opt) => (
          <label
            key={opt.value}
            className={`flex h-9 items-center gap-2 rounded-control border px-2.5 text-[13px] ${draft.onMatchAction === opt.value ? 'border-primary bg-primary-soft font-semibold' : 'border-border text-text-secondary'}`}
          >
            <input type="radio" name="dup-action" checked={draft.onMatchAction === opt.value} onChange={() => save({ onMatchAction: opt.value })} style={{ accentColor: 'var(--color-primary)' }} />
            {opt.label}
          </label>
        ))}
        {draft.onMatchAction === 'merge' && (
          <p className="text-xs text-text-muted">*Not implemented — treated as &ldquo;Warn&rdquo; today. Auto-merging records without a human choosing is a real-data-loss risk this app won&rsquo;t take silently.</p>
        )}
      </fieldset>
    </section>
  );
}

/** docs section 8 "Lead Management" — Admin ▸ Leads
 * (design/orion-crm-design/rendered-html/AdminLeadConfig.html). Only the
 * Statuses tab (plus the Assignment rules/Duplicate detection sidebar) is
 * pixel-matched — the design's screenshot only captures that one tab state,
 * and Fields/Scoring/Assignment rules/Duplicates as tabs have no captured
 * layout to match. */
export function LeadConfigPage() {
  const { data: statuses } = useLeadStatusesQuery();
  const [tab, setTab] = useState<TabKey>('statuses');
  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Leads' }];

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Leads</h1>
            <p className="text-sm leading-5 text-text-muted">Define the lead lifecycle, where leads come from and how they are routed to your team.</p>
          </div>
        </div>

        <div role="tablist" aria-label="Lead settings" className="flex gap-6 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`flex h-10 items-center gap-1.5 border-b-2 text-sm ${tab === t.key ? 'border-primary font-semibold text-ink' : 'border-transparent font-medium text-text-muted'}`}
            >
              {t.label}
              {t.key === 'statuses' && (
                <span className="flex h-4.5 items-center rounded-full bg-primary-soft px-1.5 text-[11px] font-semibold text-primary-soft-text">{(statuses ?? []).length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === 'statuses' && (
            <div className="grid grid-cols-[576px_312px] items-start gap-4">
              <StatusesTab />
              <div className="flex flex-col gap-4">
                <AssignmentRulesCard />
                <DuplicatesCard />
              </div>
            </div>
          )}
          {tab === 'sources' && <SourcesTab />}
          {tab === 'fields' && (
            <section className="flex flex-col items-center justify-center gap-3 rounded-dialog border border-dashed border-border-strong bg-surface p-12 text-center shadow-card">
              <h2 className="text-[15px] font-semibold">Custom fields on leads</h2>
              <p className="max-w-md text-sm text-text-muted">Custom fields are managed in one place for every record type.</p>
              <Link to="/admin/custom-fields" className="text-sm font-semibold text-primary">
                Go to Custom fields →
              </Link>
            </section>
          )}
          {tab === 'scoring' && <ComingSoon title="Lead scoring" note="Not built yet — no scoring engine exists in this app today." />}
          {tab === 'assignment' && <AssignmentRulesCard />}
          {tab === 'duplicates' && <DuplicatesCard />}
        </div>
      </main>
    </>
  );
}
